import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';

const isMatchAgainstMe = (match) => {
  if (!match) return true;
  const p1Id = match.player1_id ?? match.player1Id ?? match.match_config?.player1Id ?? match.current_state?.player1Id;
  const p1Name = match.player1_name ?? match.player1Name ?? match.match_config?.player1Name ?? match.current_state?.player1Name;
  const p2Id = match.player2_id ?? match.player2Id ?? match.match_config?.player2Id ?? match.current_state?.player2Id;
  const p2Name = match.player2_name ?? match.player2Name ?? match.match_config?.player2Name ?? match.current_state?.player2Name;

  if (p1Id && p1Id !== 'me' && String(p1Id).toLowerCase() !== 'me') {
    if (p1Name && p1Name.trim().toLowerCase() !== 'me') {
      if (p2Id === 'me' || String(p2Id).toLowerCase() === 'me' || (p2Name && p2Name.trim().toLowerCase() === 'me')) {
        return true;
      }
      return false;
    }
  }

  if (match.name && match.name.includes(' vs ')) {
    const parts = match.name.split(' vs ').map(s => s.trim().toLowerCase());
    if (parts.length === 2) {
      const hasMe = parts[0] === 'me' || parts[1] === 'me';
      if (!hasMe && p1Id && p1Id !== 'me' && String(p1Id).toLowerCase() !== 'me') {
        return false;
      }
    }
  }

  return true;
};

const RecentMatches = ({ matches = [] }) => {
  const navigate = useNavigate();

  const handleMatchClick = (match) => {
    if (!match) return;
    const statusStr = String(match.status || '').toLowerCase();
    const winnerStr = String(match.result || match.winner || '').toLowerCase();
    const isCompleted = statusStr === 'completed' || statusStr === 'finished' || match.current_state?.matchCompleted === true || (Boolean(winnerStr) && winnerStr !== 'inprogress' && winnerStr !== 'in_progress' && winnerStr !== 'pending');
    if (!isCompleted) {
      navigate(`/match-tracker/${match.match_id || match.id}`, { state: { isContinuedMatch: true } });
    } else {
      navigate(`/match-details/${match.match_id || match.id}`);
    }
  };

  const getFormattedScore = (match) => {
    if (!match) return '0-0';
    let scoreText = '';
    let matchScores = [];

    if (match.match_scores_json) {
      try {
        matchScores = typeof match.match_scores_json === 'string' ? JSON.parse(match.match_scores_json) : match.match_scores_json;
      } catch (e) {}
    }

    let cs = null;
    if (match.current_state) {
      try {
        cs = typeof match.current_state === 'string' ? JSON.parse(match.current_state) : match.current_state;
      } catch (e) {}
    }
    if (!matchScores.length && cs?.matchScores) {
      matchScores = cs.matchScores;
    }

    if (Array.isArray(matchScores) && matchScores.length > 0) {
      scoreText = matchScores.map(s => {
        const tb = s?.tbScore ? ` (${s.tbScore})` : '';
        return `${s?.myGames || s?.player1_games || 0}-${s?.opponentGames || s?.player2_games || 0}${tb}`;
      }).join(', ');
    } else if (match.score && match.score !== '0-0') {
      scoreText = match.score;
    }

    const isMatchDone = String(match.status || '').toLowerCase() === 'completed' || match.current_state?.matchCompleted === true || (Boolean(match.winner) && String(match.winner).toLowerCase() !== 'inprogress' && String(match.winner).toLowerCase() !== 'in_progress');
    const isInProg = !isMatchDone;
    if (isInProg) {
      if (cs && ((cs.myGamesWonInSet || 0) > 0 || (cs.opponentGamesWonInSet || 0) > 0)) {
        const activeScore = `${cs.myGamesWonInSet || 0}-${cs.opponentGamesWonInSet || 0}`;
        const parts = scoreText ? scoreText.split(',').map(s => s.trim()).filter(Boolean) : [];
        const setNum = parseInt(cs.setNumber, 10) || 1;

        if (setNum > parts.length) {
          parts.push(activeScore);
        } else if (setNum === parts.length) {
          parts[parts.length - 1] = activeScore;
        }
        scoreText = parts.join(', ');
      }
    }

    return scoreText || match.score || '0-0';
  };

  const safeMatches = Array.isArray(matches) ? matches : [];

  return (
    <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0 mb-4">
        <h2 className="text-xl font-semibold text-gray-800">Recent Matches</h2>
        <NavLink to="/new-match" className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-md transition duration-300 w-full sm:w-auto text-center">+ Add Match</NavLink>
      </div>
      <div className="space-y-4">
        {safeMatches.slice(0, 5).map((match, index) => {
          if (!match) return null;
          const statusStr = String(match.status || '').toLowerCase();
          const winnerStr = String(match.result || match.winner || '').toLowerCase();
          const isCompleted = statusStr === 'completed' || match.current_state?.matchCompleted === true || (Boolean(winnerStr) && winnerStr !== 'inprogress' && winnerStr !== 'in_progress');
          const isInProgress = !isCompleted;
          
          const isMeWinner = (() => {
            const w = String(match.winner || match.result || '').trim().toLowerCase();
            if (w === 'me' || w === 'player1' || w === 'won') return true;
            if (w === 'opponent' || w === 'player2' || w === 'lost') return false;
            if (match.won === true) return true;
            if (match.won === false) return false;
            const mySets = parseInt(match.my_sets_won ?? match.mySetsWon ?? match.current_state?.mySetsWon ?? 0, 10);
            const oppSets = parseInt(match.opponent_sets_won ?? match.opponentSetsWon ?? match.current_state?.opponentSetsWon ?? 0, 10);
            if (mySets > oppSets) return true;
            if (oppSets > mySets) return false;
            let mScores = [];
            if (match.match_scores_json) {
              try { mScores = typeof match.match_scores_json === 'string' ? JSON.parse(match.match_scores_json) : match.match_scores_json; } catch(e) {}
            }
            if (!mScores.length && match.current_state?.matchScores) {
              mScores = match.current_state.matchScores;
            }
            if (Array.isArray(mScores) && mScores.length > 0) {
              let s1 = 0, s2 = 0;
              mScores.forEach(s => {
                const g1 = parseInt(s.myGames || s.player1_games || 0, 10);
                const g2 = parseInt(s.opponentGames || s.player2_games || 0, 10);
                if (g1 > g2) s1++;
                else if (g2 > g1) s2++;
              });
              if (s1 > s2) return true;
              if (s2 > s1) return false;
            }
            if (match.score && typeof match.score === 'string' && match.score !== '0-0') {
              const parts = match.score.split(/[\s,]+/).filter(Boolean);
              let p1 = 0, p2 = 0;
              parts.forEach(pt => {
                const [g1, g2] = pt.split('-').map(n => parseInt(n, 10) || 0);
                if (g1 > g2) p1++;
                else if (g2 > g1) p2++;
              });
              if (p1 > p2) return true;
              if (p2 > p1) return false;
            }
            return true;
          })();

          const isAgainstMe = isMatchAgainstMe(match);

          let opponentDisplayName = 'Opponent';
          if (match.opponent_name) {
            opponentDisplayName = match.opponent_name;
          } else if (match.opponent) {
            opponentDisplayName = match.opponent;
          }

          let matchTitle = `vs ${opponentDisplayName}`;
          if (match.name) {
            matchTitle = match.name.includes(' vs ') ? match.name : match.name;
          }

          let matchDateStr = new Date().toLocaleDateString();
          if (match.date) {
            matchDateStr = match.date;
          } else if (match.created_at || match.match_date) {
            try {
              matchDateStr = new Date(match.created_at || match.match_date).toLocaleDateString();
            } catch (e) {}
          }

          return (
            <div
              key={match.match_id || match.id || index}
              className="flex flex-col sm:grid sm:grid-cols-3 gap-2 sm:gap-0 sm:items-center p-4 rounded-lg bg-gray-50 shadow-sm cursor-pointer hover:bg-gray-100 transition duration-150 ease-in-out border border-gray-100"
              onClick={() => handleMatchClick(match)}
            >
              <div className="font-semibold flex items-center gap-2">
                {isInProgress ? (
                  <span className="px-3 py-1 rounded-full text-xs font-bold text-white bg-blue-600">
                    In Progress
                  </span>
                ) : (
                  isAgainstMe && (
                    <span className={`px-3 py-1 rounded-full text-xs font-bold text-white ${isMeWinner ? 'bg-green-500' : 'bg-red-500'}`}>
                      {isMeWinner ? 'Won' : 'Lost'}
                    </span>
                  )
                )}
                <span className="truncate text-gray-800">
                  {matchTitle}
                </span>
              </div>
              <div className="text-gray-600 text-sm order-3 sm:order-none">
                <span className="truncate">{match.surface || match.location || 'Hard'} • {matchDateStr}</span>
              </div>
              <div className="font-semibold sm:text-right text-gray-900">
                <span>{getFormattedScore(match)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RecentMatches;
